import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Chart from 'react-apexcharts';
import { useNavigate } from 'react-router-dom';
import Loader from '../../../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../../../SleepHub/sleepHubApi';
import { getLocalStorageItem } from '../../../../utils/helper';
import { fetchCompanyGamesOverview } from './companyGamesApi';
import SuppressedPanel from './SuppressedPanel';
import LoadFailedPanel from './LoadFailedPanel';
import BarList from './GameBars';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  PANEL_ROW_CLASS,
  STAT_ICONS,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID
} from '../../../../component/common/designSystem';
import { GAME_PATHS, gameColor, BREADTH_RAMP } from './gamesTheme';

/**
 * Games → Overview.
 *
 * Three things carry this page, and the order matters:
 *
 *  1. HOW MANY PEOPLE PLAY ANYTHING. De-duplicated across all four, so somebody
 *     who plays chess and the quiz is one person who games, not two.
 *  2. HOW BROADLY THEY RANGE. The breadth split is the most useful shape here:
 *     a section where almost everybody sits in "One game" has four games and
 *     one audience, which is a completely different product situation from four
 *     games people move between.
 *  3. WHETHER THEY COME BACK. Take-up without return is a novelty; the return
 *     rate is what separates a feature people use from one they tried.
 *
 * WHY TAKE-UP IS A BAR CHART AND VOLUME IS A DONUT WITH A CAVEAT. Take-up per
 * game shares one denominator — the whole population — so the four bars are
 * genuinely comparable. Play counts are NOT: a Block Fill level takes seconds
 * and a chess game takes minutes, so the volume donut is labelled with each
 * game's own unit and the page says so in as many words.
 *
 * Nobody is named on this page or reachable from it, there is no ranking of
 * players anywhere, and a scope with too few players renders the withheld state
 * instead of numbers.
 *
 * `fetcher` is a prop so an unscoped hub could render this page unchanged.
 */
const GamesOverview = ({ fetcher = fetchCompanyGamesOverview, populationLabel = 'users' }) => {
  const navigate = useNavigate();
  const userData = JSON.parse(getLocalStorageItem('userData') || '{}');
  const slug = userData?.slug;

  const [loader, setLoader] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoader(true);
    fetcher().then((data) => {
      if (!mounted) return;
      setStats(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
  }, [fetcher]);

  if (loader) return <Loader />;

  // Checked before suppression: a failed request is not a small cohort, and
  // showing the withheld state for it would claim a protection that never ran.
  if (stats?.error) return <LoadFailedPanel />;

  // The whole page is withheld, not just a panel: with a cohort this small
  // every figure on it would be about identifiable people.
  if (stats?.suppressed) {
    return <SuppressedPanel minimum={stats.minimum} what="any of the games" />;
  }

  const games = stats?.games || [];
  const trendKeys = stats?.trendKeys || [];
  const withheld = stats?.withheld || [];
  const untouched = stats?.untouched || [];

  const trendLength = trendKeys.length ? (stats?.trend?.[trendKeys[0].key] || []).length : 0;
  const trendHasData = trendKeys.some((entry) =>
    (stats?.trend?.[entry.key] || []).some((day) => day.plays > 0)
  );

  const trendOptions = {
    chart: { ...CHART_BASE, type: 'line' },
    colors: trendKeys.map((entry) => gameColor(entry.key)),
    stroke: { width: 3, curve: 'smooth' },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: lastNDaysLabels(trendLength), tickAmount: 6 },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } },
    tooltip: { shared: true }
  };

  /**
   * Take-up per game as one horizontal bar. All four share the SAME
   * denominator — the whole population — which is what makes them comparable
   * when the play counts are not.
   */
  const adoptionOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [({ dataPointIndex }) => gameColor(games[dataPointIndex]?.key)],
    plotOptions: {
      bar: { horizontal: true, borderRadius: 3, barHeight: '45%', distributed: true }
    },
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: CHART_GRID,
    xaxis: {
      categories: games.map((row) => row.label),
      labels: { formatter: (val) => `${Math.round(val)}%` },
      max: 100
    },
    tooltip: {
      y: {
        formatter: (val, { dataPointIndex }) =>
          `${val}% — ${formatBigNumber(games[dataPointIndex]?.players || 0)} people`
      }
    }
  };

  const weekdayOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: [CHART_COLORS.brand],
    plotOptions: { bar: { borderRadius: 4, columnWidth: '45%' } },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    xaxis: { categories: (stats?.weekday || []).map((row) => row.label) },
    yaxis: { labels: { formatter: (val) => formatBigNumber(val) } }
  };

  return (
    <div>
      {/* ---------- Headline ---------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4">
        <StatCard
          icon={STAT_ICONS.users}
          title="People Playing"
          value={formatBigNumber(stats?.players || 0)}
        />
        <StatCard
          icon={STAT_ICONS.activeUsers}
          title={`Share of ${populationLabel}`}
          value={stats?.totalUsers > 0 ? `${stats.adoption}%` : '-'}
        />
        {/* The breadth headline. Four games and an average near 1.0 means four
            separate small audiences, not one engaged one. */}
        <StatCard
          icon={STAT_ICONS.catalog}
          title="Games Tried Each"
          value={`${stats?.averageGamesTried ?? 0} of ${stats?.gamesAvailable ?? 0}`}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Times Played"
          value={formatBigNumber(stats?.totalPlays || 0)}
        />
        <StatCard
          icon={STAT_ICONS.sessions}
          title="Played — Last 30 Days"
          value={formatBigNumber(stats?.recentPlays || 0)}
        />
        {/* Played this week having also played the week before. Null when
            nobody was active in the earlier window to come back from. */}
        <StatCard
          icon={STAT_ICONS.streak}
          title="Came Back"
          value={stats?.returnRate == null ? '-' : `${stats.returnRate}%`}
        />
      </div>

      {/* A total that omits a withheld game is a partial total, and says so
          rather than letting itself be read as the whole picture. */}
      {!stats?.playsAreComplete ? (
        <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
          &ldquo;Times played&rdquo; covers the {stats?.gamesReported} games shown below. A game
          with too few players is left out of it entirely — including its plays would let the hidden
          figure be worked out by subtraction.
        </p>
      ) : null}

      {/* ---------- Shape of it ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <MiniDonutCard
          title="How Many Games Each"
          subtitle="People who have tried one, two, three or all four"
          data={stats?.breadth || []}
          colors={BREADTH_RAMP}
          unit="people"
          totalLabel="Players"
          legendLimit={4}
        />
        <Panel title="Take-up by Game" subtitle={`Share of your ${populationLabel} on each one`}>
          {games.length > 0 && stats?.totalUsers > 0 ? (
            <Chart
              type="bar"
              height={260}
              width="100%"
              series={[{ name: 'Take-up', data: games.map((row) => row.adoption) }]}
              options={adoptionOptions}
            />
          ) : (
            <PanelEmpty>Nothing to report at this group size.</PanelEmpty>
          )}
        </Panel>
        <Panel title="Playing Over Time" subtitle="Plays per day — last 30 days">
          {trendHasData ? (
            <Chart
              type="line"
              height={260}
              width="100%"
              series={trendKeys.map((entry) => ({
                name: entry.label,
                data: (stats?.trend?.[entry.key] || []).map((day) => day.plays || 0)
              }))}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>Nothing played in the last 30 days.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Volume, with the units kept honest ---------- */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <Panel
          title="Where the Playing Goes"
          subtitle="Share of all plays — note each game counts a different thing"
        >
          {(stats?.playSplit || []).length > 0 ? (
            <>
              <BarList
                rows={stats.playSplit}
                unitFor={(row) => row.unit || 'plays'}
                colorFor={(_index, _label, row) => gameColor(row.key)}
              />
              <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
                A Block Fill level takes seconds and a chess game takes minutes, so these shares say
                where the activity is, not where the time goes. Player counts below are the
                like-for-like comparison.
              </p>
            </>
          ) : (
            <PanelEmpty>Nothing played yet.</PanelEmpty>
          )}
        </Panel>
        <MiniDonutCard
          title="Players by Game"
          subtitle="One person can appear in more than one"
          data={stats?.playerSplit || []}
          colors={(stats?.playerSplit || []).map((row) => gameColor(row.key))}
          unit="players"
          totalLabel="Entries"
          legendLimit={4}
        />
        <Panel title="Which Day People Play" subtitle="All games, whole period">
          {(stats?.weekday || []).some((row) => row.count > 0) ? (
            <Chart
              type="bar"
              height={260}
              width="100%"
              series={[{ name: 'Plays', data: (stats?.weekday || []).map((row) => row.count) }]}
              options={weekdayOptions}
            />
          ) : (
            <PanelEmpty>Nothing played yet.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* ---------- Side by side ---------- */}
      <div className="mt-6">
        <Panel
          title="Game by Game"
          subtitle="Reach, how much each player uses it, and whether they return"
        >
          {games.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Game</th>
                  <th className="py-2 px-4 font-medium text-center">Players</th>
                  <th className="py-2 px-4 font-medium text-center">Take-up</th>
                  <th className="py-2 px-4 font-medium text-center">Played</th>
                  <th className="py-2 px-4 font-medium text-center">Each</th>
                  <th className="py-2 px-4 font-medium text-center">Came back</th>
                  <th className="py-2 font-medium text-right">Last played</th>
                </>
              }
            >
              {games.map((row) => (
                <tr
                  key={row.key}
                  className={`${PANEL_ROW_CLASS} cursor-pointer`}
                  onClick={() => navigate(`/${slug}/games/${GAME_PATHS[row.key]}`)}
                >
                  <td className="py-2 pr-4">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: gameColor(row.key) }}
                      />
                      {row.label}
                    </span>
                  </td>
                  <td className="py-2 px-4 text-center">{formatBigNumber(row.players)}</td>
                  <td className="py-2 px-4 text-center">{row.adoption}%</td>
                  {/* Each game's own unit rides along with its count, so the
                      column never implies four numbers are the same thing. */}
                  <td className="py-2 px-4 text-center">
                    {formatBigNumber(row.plays)}
                    <span className="ml-1.5 text-gray-400 font-normal">{row.unit}</span>
                  </td>
                  <td className="py-2 px-4 text-center">
                    {row.playsPerPlayer}
                    <span className="ml-1.5 text-gray-400 font-normal">{row.unit}</span>
                  </td>
                  <td className="py-2 px-4 text-center">
                    {row.returnRate == null ? '-' : `${row.returnRate}%`}
                  </td>
                  <td className="py-2 text-right">{row.lastPlayed || '-'}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No game has enough players to report.</PanelEmpty>
          )}
        </Panel>
      </div>

      {/* A game can be withheld for being too small while the section as a
          whole reports — say so, rather than letting it look unused. Games
          nobody has opened are a separate, and equally useful, fact. */}
      {withheld.length > 0 ? (
        <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
          {withheld.map((row) => row.label).join(', ')} {withheld.length === 1 ? 'is' : 'are'} not
          shown separately: fewer than {stats?.minimum} people in this view have played{' '}
          {withheld.length === 1 ? 'it' : 'them'}, so any figure would be about identifiable
          individuals.
        </p>
      ) : null}

      {untouched.length > 0 ? (
        <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
          Nobody in this view has played {untouched.map((row) => row.label).join(' or ')}.
        </p>
      ) : null}

      <p className="mt-3 text-xs text-gray-400 dark:text-gray-500">
        No individual is named in this section and there is no ranking of players anywhere in it.
        Scores, win rates and levels reached appear only as figures about the group — the app has
        its own leaderboards, which players choose to take part in.
      </p>
    </div>
  );
};

GamesOverview.propTypes = {
  fetcher: PropTypes.func,
  populationLabel: PropTypes.string
};

export default GamesOverview;
